import React, { useEffect, useState } from 'react';
import { Button as SemanticButton, Icon, Message, Modal } from 'semantic-ui-react';

import { getCommissionCollaboratorDeletionCheck } from '../../../services/standalone-charges';
import { CommissioningCollaborator } from '../types';

type Props = {
  open: boolean;
  collaborator: CommissioningCollaborator | null;
  loading?: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteCollaboratorModal: React.FC<Props> = ({
  open,
  collaborator,
  loading = false,
  onClose,
  onConfirm
}) => {
  const [checking, setChecking] = useState(false);
  const [canDelete, setCanDelete] = useState<boolean | null>(null);
  const [blockReason, setBlockReason] = useState('');
  const [checkError, setCheckError] = useState('');

  useEffect(() => {
    if (!open || !collaborator?.id) {
      setChecking(false);
      setCanDelete(null);
      setBlockReason('');
      setCheckError('');
      return;
    }

    let cancelled = false;

    const loadDeletionCheck = async () => {
      setChecking(true);
      setCanDelete(null);
      setBlockReason('');
      setCheckError('');

      try {
        const result = await getCommissionCollaboratorDeletionCheck(collaborator.id);
        if (cancelled) return;

        setCanDelete(result.canDelete);
        setBlockReason(
          result.reason ||
            'Este colaborador não pode ser excluído pois já existem cobranças lançadas para ele.'
        );
      } catch {
        if (cancelled) return;
        setCanDelete(null);
        setCheckError('Não foi possível verificar se o colaborador pode ser excluído.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    };

    loadDeletionCheck();

    return () => {
      cancelled = true;
    };
  }, [open, collaborator?.id]);

  const isBlocked = canDelete === false;
  const confirmDisabled = loading || checking || canDelete !== true;

  return (
    <Modal open={open} onClose={onClose} size="tiny" closeIcon>
      <Modal.Header>
        <Icon name="trash alternate" /> Excluir colaborador
      </Modal.Header>
      <Modal.Content>
        {checking && (
          <p style={{ color: '#4f5661', fontSize: 13, margin: 0 }}>
            Verificando se o colaborador pode ser excluído...
          </p>
        )}

        {!checking && checkError && <Message negative>{checkError}</Message>}

        {!checking && isBlocked && (
          <Message warning>
            <Message.Header>Exclusão não permitida</Message.Header>
            <p style={{ margin: '8px 0 0' }}>{blockReason}</p>
          </Message>
        )}

        {!checking && canDelete === true && (
          <>
            <h4 style={{ margin: '0 0 10px' }}>
              Você está prestes a excluir um colaborador comissionado.
            </h4>
            <p style={{ color: '#4f5661', fontSize: 13, lineHeight: 1.45, margin: 0 }}>
              Ao confirmar a exclusão, esse colaborador não será mais exibido como apto
              para receber comissionamento de cobranças. Deseja continuar?
            </p>
          </>
        )}

        {collaborator && (
          <p style={{ color: '#666', fontSize: 13, margin: '12px 0 0' }}>
            Colaborador: <strong>{collaborator.name}</strong>
          </p>
        )}
      </Modal.Content>
      <Modal.Actions>
        <SemanticButton
          type="button"
          color="red"
          onClick={onClose}
          disabled={loading}
        >
          <Icon name="remove" /> {isBlocked || checkError ? 'Fechar' : 'Cancelar'}
        </SemanticButton>
        {!isBlocked && !checkError && (
          <SemanticButton
            type="button"
            color="green"
            onClick={onConfirm}
            loading={loading || checking}
            disabled={confirmDisabled}
          >
            <Icon name="check" /> Confirmar
          </SemanticButton>
        )}
      </Modal.Actions>
    </Modal>
  );
};

export default DeleteCollaboratorModal;
